import React, { useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Lock, Eye, EyeOff } from "lucide-react"
import { resetPassword } from "../api/apiClient"

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const [token, setToken] = useState("")
  const [email, setEmail] = useState("")
  
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  
  const [status, setStatus] = useState("idle")
  const [message, setMessage] = useState("")

  useEffect(() => {
    const tokenParam = searchParams.get("token")
    const emailParam = searchParams.get("email")
    
    if (tokenParam) setToken(tokenParam)
    if (emailParam) setEmail(emailParam)
    
    if (!tokenParam || !emailParam) {
      setStatus("error")
      setMessage("Invalid password reset link.")
    }
  }, [searchParams])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (newPassword !== confirmPassword) {
      setStatus("error")
      setMessage("Passwords do not match.")
      return
    }

    if (newPassword.length < 6) {
      setStatus("error")
      setMessage("Password must be at least 6 characters.")
      return
    }

    setStatus("loading")
    setMessage("")

    try {
      await resetPassword({ email, token, newPassword })
      setStatus("success")
      setMessage("Password reset successfully. You can now log in.")
    } catch (err) {
      setStatus("error")
      setMessage(err.message || "Failed to reset password.")
    }
  }

  if (status === "success") {
    return (
      <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-2xl">
        <div className="text-center">
          <div className="bg-green-100 text-green-700 p-4 rounded-lg mb-6">
            {message}
          </div>
          <button
            onClick={() => navigate("/login")}
            className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition duration-150 shadow-md"
          >
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-2xl">
      <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center flex items-center justify-center">
        <Lock className="w-6 h-6 mr-2 text-yellow-600" /> Set New Password
      </h2>

      {status === "error" && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 text-sm">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4 relative">
          <input
            className="w-full p-3 border border-gray-300 rounded-lg pr-10"
            placeholder="New Password"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        <div className="mb-6">
          <input
            className="w-full p-3 border border-gray-300 rounded-lg"
            placeholder="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        <button
          type="submit"
          disabled={status === "loading" || !token || !email}
          className="w-full py-3 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700 transition duration-150 shadow-md disabled:opacity-50"
        >
          {status === "loading" ? "Resetting..." : "Reset Password"}
        </button>
      </form>
    </div>
  )
}

export default ResetPasswordPage
